import React from 'react';
import { getSpreadDimensions } from '../../components/BookDesign/constants';
import { useDimensions } from '../../contexts/DimensionContext';
// Import border image using Vite's import.meta.url
const alphaBorderImg = new URL('../../assets/image-border/ImageAlphaChannel.png', import.meta.url).href;

export default function PreviewContent({ currentPage, totalPages, pageContents, onContentChange, step = 1, isClearing = false }) {
  // Force re-processing on each render with a component-level timestamp
  const renderTimestamp = Date.now();
  
  // Log the incoming pageContents for debugging
  console.log(`PreviewContent rendering at ${renderTimestamp} with:`, { 
    currentPage, 
    totalPages, 
    pageContentsLength: pageContents?.length || 0,
    step,
    isClearing,
    sample: pageContents && pageContents.length > 0 ? pageContents[0]?.substring(0, 30) : 'empty'
  });
  
  // If isClearing is true, we force empty content to ensure UI shows blank during transition
  if (isClearing) {
    console.log('CLEARING MODE ACTIVE - forcing empty content display');
    // Return early with completely empty content
    return (
      <div style={{ padding: '20px' }}>
        <div className="preview-page-content" style={{ minHeight: '300px', padding: '20px' }}>
          <p>Clearing content...</p>
        </div>
      </div>
    );
  }
  // Get dimensions from context instead of hardcoded value
  const { selectedPageSize, dimensions: contextDimensions, getRawDimensions } = useDimensions();
  
  // Use either the dimensions from context or fallback to the getSpreadDimensions function
  const rawDimensions = getRawDimensions();
  const dimensions = contextDimensions || getSpreadDimensions(selectedPageSize);
  const scale = 0.8;  // This could also be dynamic based on screen size
  
  // Log the dimensions being used
  console.log(`Using page dimensions for ${selectedPageSize}:`, rawDimensions);
  
  // For step 1, we directly use the pageContents which already has the contents
  // properly arranged (with content at odd-numbered indices)
  let pageContent = '';
  let actualPageNumber = 0;
  
  if (step === 1) {
    // For step 1, we're showing the odd-numbered pages which are pre-processed
    // The currentPage is the index in the sequence of odd-numbered pages (1-based)
    // We need to find the actual odd-numbered page index
    let oddPageCount = 0;
    let foundIndex = -1;
    
    // Log the page contents to debug what we're working with
    console.log('Step 1 content processing - pageContents:');
    pageContents.forEach((content, idx) => {
      if (content) {
        console.log(`Page ${idx+1} (${(idx+1) % 2 === 1 ? 'odd' : 'even'}): ${content.substring(0, 30)}...`);
      }
    });
    
    // Find the index of the nth odd-numbered page with content
    for (let i = 0; i < pageContents.length; i++) {
      // Check if this is an odd-numbered page with content
      if ((i + 1) % 2 === 1 && pageContents[i]) {
        oddPageCount++;
        console.log(`Found odd page ${i+1} (${oddPageCount}) with content: ${pageContents[i].substring(0, 30)}...`);
        
        // If this is the currentPage-th odd page, this is the one we want
        if (oddPageCount === currentPage) {
          foundIndex = i;
          console.log(`Using content from page ${i+1} for display`);
          break;
        }
      }
    }
    
    console.log(`Total odd pages with content: ${oddPageCount}, selected index: ${foundIndex}`);
    
    if (foundIndex >= 0) {
      pageContent = pageContents[foundIndex];
      console.log(`Selected content: ${pageContent.substring(0, 50)}...`);
      actualPageNumber = foundIndex + 1; // +1 to convert from 0-based to 1-based
    } else {
      console.log('No matching odd page found with content!');
    }
  } else {
    // Original behavior for other steps
    const actualPageIndex = (currentPage - 1);
    pageContent = pageContents?.[actualPageIndex] || '';
    actualPageNumber = currentPage;
  }
  
  const isImagePage = step === 1 ? false : currentPage % 2 === 0;
  
  // Count the number of odd-numbered pages with content for pagination
  const oddNumberedPagesWithContent = step === 1 
    ? pageContents.filter((content, index) => content && (index + 1) % 2 === 1).length
    : totalPages;

  return (
    <div 
      className="bg-white relative mx-auto p-6"
      style={{
        width: dimensions.width * scale,
        height: dimensions.height * scale,
        margin: '0 auto',
        position: 'relative'
      }}
    >
      {/* Simple container for content */}
      <div style={{ 
          position: 'relative', 
          width: '100%',
          height: '100%',
          background: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
        }}>
        {/* Content layer */}
        <div style={{ 
          position: 'relative',
          zIndex: 1,
          height: '100%',
          width: '100%',
        }}>
          {isImagePage ? (
            <div style={{
              height: '100%', 
              aspectRatio: '3/4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(249, 250, 251, 0.8)', // Slightly transparent background
              borderRadius: '8px',
              padding: '10px',
            }}>
              <p style={{ color: '#9CA3AF' }}>Image will be generated for this page</p>
            </div>
          ) : (
            <textarea
              value={pageContent}
              onChange={(e) => onContentChange?.(actualPageNumber - 1, e.target.value)}
              readOnly={false} // Explicitly set to editable
              style={{
                width: '100%',
                height: '100%',
                aspectRatio: '3/4',
                padding: '24px',
                color: '#1F2937',
                resize: 'none',
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.95)', // Slightly transparent
                cursor: 'text', // Show text cursor
              }}
              placeholder="Enter text for this page..."
            />
          )}
        </div>

        {/* Dark Overlay Frame for maximum contrast */}
        <div style={{
          position: 'absolute',
          top: '-10px',  // Extend slightly beyond the container
          left: '-10px',
          right: '-10px',
          bottom: '-10px',
          zIndex: 20,
          pointerEvents: 'none',
          border: '15px solid rgba(0,0,0,0.7)', // Dark visible border
          borderRadius: '16px',
          boxShadow: 'inset 0 0 30px 15px rgba(0,0,0,0.6)', // Strong inner shadow
        }}></div>
        
        {/* Alpha channel image with different blend mode */}
        {/* Make sure the image doesn't block interaction with the textarea */}
        <img 
          src={alphaBorderImg}
          alt="Border"
          style={{
            position: 'absolute',
            top: '-5px',
            left: '-5px',
            width: 'calc(100% + 10px)',
            height: 'calc(100% + 10px)',
            objectFit: 'cover',
            zIndex: 10,
            mixBlendMode: 'overlay', // Try different blend mode
            filter: 'contrast(200%) brightness(150%)', // Enhance contrast and brightness
            opacity: 1,
            pointerEvents: 'none',
          }}
        />
      </div>


      {/* Page number overlay */}
      <div className="absolute bottom-4 right-4 text-sm text-gray-500">
        {step === 1 ? (
          <span>Preview {currentPage} of {oddNumberedPagesWithContent + 1}</span>
        ) : (
          <span>Page {currentPage} of {oddNumberedPagesWithContent}</span>
        )}
      </div>
    </div>
  );
}